import {createApiClient, type HttpClientInstance} from "@/infrastructure/http";
import {ServerRouter, type ServerRouterInstance} from "@/infrastructure/navigation";
import {proxy, ref} from "valtio";

import {IQueryParamProvider, IServerNavigable} from "@/shared/contracts";
import {EventEmitter} from "@/shared/event-emitter";
import {IValtioModel} from "@/shared/models/base-model.interface";
import {Field, type FieldInstance} from "@/shared/models/form/fields";
import {type BaseValidator} from "@/shared/models/form/fields/validators";
import {QueryState, type QueryStateInstance} from "@/shared/services";
import {routes} from "@/shared/utils";
import {isClient} from "@/shared/utils/runtime";
import {slugGetLocation} from "@/shared/utils/slug-location";

export const ATTACH_EVENTS = Symbol("attachEvents");

// US State abbreviations mapping

export interface LocationOption {
    id: string;
    name: string;
    type: "country" | "state" | "destination" | "city" | "rental";
    state?: string;
    slug: string;
    url: string;
    params?: string;
}

interface LocationSearchConfig {
    initialValue?: string;
    initialSelectedOption?: LocationOption;
    debounceDelay?: number;
    validators?: Array<BaseValidator>;
    /**
     * Auto redirect to rental page when rental is selected
     * @default true
     */
    autoRedirect?: boolean;
}

interface LocationSearchEvents {
    select: (option: LocationOption) => void;
    clear: () => void;
}

interface LocationsApiResponse {
    countries: Array<LocationOption>;
    states: Array<LocationOption>;
    destinations: Array<LocationOption>;
    cities: Array<LocationOption>;
    rentals: Array<LocationOption>;
}

export class LocationSearchClass implements IValtioModel, IQueryParamProvider, IServerNavigable {
    // Reactive properties
    input: FieldInstance;
    selectedOption: LocationOption | null;
    isOpen: boolean;
    queryState: QueryStateInstance<LocationsApiResponse, [string]>;
    serverRouter: ServerRouterInstance;
    error: string | null;
    pendingSlug: string | null; // Slug waiting to be fetched in init()
    initialModel: boolean; // Flag to track if model was initialized

    private lastQuery: string = "";
    private apiClient: HttpClientInstance;
    private readonly autoRedirect: boolean;
    _suppressNextBlur: boolean = false;

    public readonly events: EventEmitter<LocationSearchEvents>;
    protected readonly validators: Array<BaseValidator>;

    constructor(config?: LocationSearchConfig) {
        // Initialize Field - use initial selected option name if provided
        this.input = Field({
            initialValue: config?.initialSelectedOption?.name ?? config?.initialValue ?? "",
        });

        // Initialize reactive properties
        this.selectedOption = config?.initialSelectedOption ?? null;
        this.isOpen = false;
        this.error = null;
        this.pendingSlug = null; // No pending slug initially
        this.initialModel = false; // Not initialized yet
        this.apiClient = createApiClient();
        this.serverRouter = ServerRouter();

        // Config options
        this.autoRedirect = config?.autoRedirect ?? true;

        // Validators (non-reactive)
        this.validators = ref(config?.validators || []) as Array<BaseValidator>;

        this.queryState = QueryState<LocationsApiResponse, [string]>(
            async (signal, query) => {
                return this.apiClient.get<LocationsApiResponse>(`/locations?query=${encodeURIComponent(query)}`, {signal});
            },
            {debounceDelay: config?.debounceDelay ?? 500},
        );

        // Private properties
        this.events = ref(new EventEmitter<LocationSearchEvents>()) as EventEmitter<LocationSearchEvents>;
    }

    [ATTACH_EVENTS](): void {
        // Attach event listeners
        this.input.events.on("change", (value: string) => {
            if (this.lastQuery !== value && value.length > 2) {
                this.isOpen = true;
                this.lastQuery = value;
                void this.queryState.execute(value.trim());
            }
        });

        this.input.events.on("focus", () => {
            this.clearError();
            void this.onFocus();
        });
    }

    // Getters - cached automatically in snapshot (Valtio recommended pattern)
    get filteredOptions(): {
        countries: Array<LocationOption>;
        states: Array<LocationOption>;
        destinations: Array<LocationOption>;
        cities: Array<LocationOption>;
        rentals: Array<LocationOption>;
    } {
        const data = this.queryState.data;

        return {
            countries: data?.countries || [],
            states: data?.states || [],
            destinations: data?.destinations || [],
            cities: data?.cities || [],
            rentals: data?.rentals || [],
        };
    }

    get value(): string {
        return this.input.value;
    }

    get slug(): string {
        return this.selectedOption?.slug || this.pendingSlug || "";
    }
    get hasResults(): boolean {
        const data = this.queryState.data;

        return (
            (data?.countries?.length || 0) > 0 ||
            (data?.states?.length || 0) > 0 ||
            (data?.destinations?.length || 0) > 0 ||
            (data?.cities?.length || 0) > 0 ||
            (data?.rentals?.length || 0) > 0
        );
    }

    get hasEmptyResults(): boolean {
        return this.isOpen && !this.hasResults && !this.queryState.isPending && this.queryState.status !== null;
    }

    get shouldShowLoading(): boolean {
        return this.queryState.isPending || this.pendingSlug !== null;
    }

    // Check if location has value
    get hasValue(): boolean {
        return !!this.input.value || this.selectedOption !== null;
    }

    get hasError(): boolean {
        return this.error !== null;
    }

    // Get query param string
    getQueryParam(paramName = "location"): string {
        if (!this.input.value) return "";
        return `${paramName}=${encodeURIComponent(this.input.value)}`;
    }

    getQueryParamSlug(paramName = "location"): string {
        if (!this.slug) return "";
        return `${paramName}=${encodeURIComponent(this.slug)}`;
    }

    // Actions
    selectOption(option: LocationOption): void {
        // If rental selected and autoRedirect enabled - navigate to rental page
        if (option.type === "rental" && this.autoRedirect) {
            this.isOpen = false;
            const rentalUrl = routes.rental.detail.url(option.slug);
            this.serverRouter.navigate(rentalUrl);
            return;
        }

        this.selectedOption = option;

        // Format display name with state abbreviation if it's a city
        let displayText = option.name;
        if (option.type === "city" && option.state) {
            displayText = `${option.name}, ${option.state}`;
        }

        this.input.setValue(displayText, true);
        this.isOpen = false;
        this.events.emit("select", option);
    }

    async onFocus(): Promise<void> {
        this.isOpen = true;

        if (!this.hasResults && this.queryState.isIdle && this.input.value !== this.lastQuery) {
            const query = this.input.value.trim();
            await this.queryState.execute(query);
        }
    }

    close(): void {
        this.isOpen = false;
    }

    clear(silent = false): void {
        this.input.clear();
        this.selectedOption = null;
        this.error = null;
        this.pendingSlug = null; // Clear pending slug
        this.afterClear(silent);
        if (!silent) {
            this.events.emit("clear");
        }
    }

    protected afterClear(_silent: boolean): void {
        this.isOpen = false;
    }

    /**
     * Validate selectedOption against validators
     * @returns true if valid, false otherwise
     */
    validate(): boolean {
        this.error = null;

        // Use selectedOption slug for validation (empty string if no selection)
        const valueToValidate = this.selectedOption?.slug ?? "";

        for (const validator of this.validators) {
            const result = validator.validate(valueToValidate);
            if (!result.isValid) {
                this.error = result.error || validator.message;
                return false;
            }
        }

        return true;
    }

    clearError(): void {
        this.error = null;
    }

    /**
     * Set value from slug
     * Stores slug for fetching in init(), shows loading state
     *
     * @param slug - Location slug like "/usa/New-York/" or "/canada/British-Columbia/Vancouver/"
     */
    setValue(slug: string): void {
        if (slug === this.selectedOption?.slug) {
            return;
        }
        if (!slug) {
            this.clear(true);
            this.pendingSlug = null;
            return;
        }

        // Set slug synchronously so getQueryParamSlug() returns new value before fetch completes
        this.selectedOption = null;
        this.pendingSlug = slug;
        this.input.setValue("", true);

        // If model already initialized, fetch location immediately (name/display will update when done)
        if (this.initialModel) {
            void this.fetchLocationBySlug(slug);
            return;
        }

        // Otherwise slug is stored for fetching in init()
    }

    /**
     * Fetch location by slug and update input field
     * @param slug - Location slug to fetch
     */
    private async fetchLocationBySlug(slug: string): Promise<void> {
        if (!isClient()) {
            this.pendingSlug = null; // Clear on server
            return;
        }

        // Show loading state
        this.pendingSlug = slug;
        this.input.setValue("", true);

        try {
            // Parse slug to search query
            const searchQuery = slugGetLocation(slug);
            if (!searchQuery) {
                this.input.clear();
                this.selectedOption = null;
                return;
            }

            // Direct API call (bypass debounce) using queryState.executeImmediate
            await this.queryState.executeImmediate(searchQuery);

            const response = this.queryState.data;

            if (!response) {
                this.input.clear();
                this.selectedOption = null;
                return;
            }

            // Collect all locations from response
            const allLocations = [
                ...(response.countries || []),
                ...(response.states || []),
                ...(response.destinations || []),
                ...(response.cities || []),
                ...(response.rentals || []),
            ];

            // Normalize slug - remove trailing slash for comparison
            const normalizedSlug = slug.replace(/\/$/, "");

            // Find location by exact slug match (compare normalized slugs)
            const location = allLocations.find((loc) => {
                const normalizedLocSlug = loc.slug.replace(/\/$/, "");
                return normalizedLocSlug === normalizedSlug;
            });

            if (location) {
                this.selectedOption = location;
                // Format name with state abbreviation if it's a city
                let displayName = location.name;
                if (location.type === "city" && location.state) {
                    displayName = `${location.name}, ${location.state}`;
                }
                this.input.setValue(displayName, true);
                // Update lastQuery to prevent re-fetch on focus
                this.lastQuery = displayName;
            } else {
                // Fallback to parsed text if exact match not found
                this.input.setValue(searchQuery, true);
                this.selectedOption = null;
                // Update lastQuery to prevent re-fetch on focus
                this.lastQuery = searchQuery;
            }
        } catch (error) {
            console.warn("[LocationSearch] Failed to fetch location:", error);
            this.input.clear();
            this.selectedOption = null;
        } finally {
            this.pendingSlug = null; // Always clear to hide loader
        }
    }

    /**
     * Initialize model - fetch location by slug if pending, or pre-fetch results if input has initial value
     */
    async init(): Promise<void> {
        // Mark model as initialized
        this.initialModel = true;

        // Handle pending slug from setValue()
        if (this.pendingSlug) {
            await this.fetchLocationBySlug(this.pendingSlug);
            return;
        }

        // Original init logic - pre-fetch if initial value exists
        const initialValue = this.input.value.trim();
        if (initialValue.length > 2 && !this.hasResults) {
            this.lastQuery = initialValue;
            void this.queryState.execute(initialValue);
        }
    }

    dispose(): void {
        this.queryState.cancel();
        this.serverRouter.dispose();
    }
}

// Export type
export type LocationSearchInstance = LocationSearchClass;

// Factory function (SSR compatible)
export function LocationSearch(config?: LocationSearchConfig): LocationSearchInstance {
    const instance = proxy(new LocationSearchClass(config));
    // Attach event listeners after proxying (for correct `this` context)
    instance[ATTACH_EVENTS]();

    return instance;
}
